import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Slider from 'react-slick';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './Gallery.css';

const Gallery = () => {
  const [showAll, setShowAll] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);

  const allAfterImages = useMemo(() => [
    {
      url: '/images/before-after/project1-after.png',
      title: 'Front Bed Upgrade - After',
      width: '100%',
      height: '100%'
    },
    {
      url: '/images/before-after/project2-after.png',
      title: 'Lawn Recovery - After',
      width: '100%',
      height: '100%'
    },
    {
      url: '/images/before-after/project3-after.png',
      title: 'Tree Bed Renovation - After',
      width: '100%',
      height: '100%'
    },
    {
      url: '/images/after-06.png',
      title: 'Palm Feature Install - After',
      fit: 'contain',
      position: '50% 50%',
      width: '100%',
      height: '100%'
    },
    {
      url: '/images/after-02.png',
      title: 'Hedge Border Refresh - After',
      width: '100%',
      height: '100%'
    },
    {
      url: '/images/after-03.png',
      title: 'Estate Lawn Finish - After',
      width: '100%',
      height: '100%'
    },
    {
      url: '/images/after-04.png',
      title: 'Palm Bed Upgrade - After',
      width: '100%',
      height: '100%'
    },
    {
      url: '/images/after-05.png',
      title: 'Luxury Turf Cleanup - After',
      width: '100%',
      height: '100%'
    },
    {
      url: '/images/pic11.jpeg',
      title: 'Seasonal Landscaping',
      width: '100%',
      height: '100%'
    },
    {
      url: '/images/pic7.jpeg',
      title: 'Garden Transformation',
      width: '100%',
      height: '100%'
    },
    {
      url: '/images/pic13.jpeg',
      title: 'Garden Design Excellence',
      width: '100%',
      height: '100%'
    }
  ], []);

  const uniqueAfterImages = useMemo(
    () => allAfterImages.filter((image, index, self) =>
      index === self.findIndex((item) => item.url === image.url)
    ),
    [allAfterImages]
  );

  const mainImages = useMemo(() => uniqueAfterImages.slice(0, 4), [uniqueAfterImages]);
  const additionalImages = useMemo(() => uniqueAfterImages.slice(4), [uniqueAfterImages]);

  // Preload images
  useEffect(() => {
    const preloadImages = mainImages.map(image => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = image.url;
        img.onload = resolve;
        img.onerror = reject;
      });
    });

    Promise.all(preloadImages)
      .then(() => setImagesLoaded(true))
      .catch(console.error);
  }, [mainImages]);

  const settings = {
    dots: true,
    infinite: true,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: imagesLoaded,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    fade: true,
    cssEase: 'cubic-bezier(0.4, 0, 0.2, 1)',
    lazyLoad: 'progressive',
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          arrows: true
        }
      },
      {
        breakpoint: 768,
        settings: {
          arrows: false,
          autoplaySpeed: 4000
        }
      }
    ]
  };

  const toggleShowAll = useCallback(() => {
    setShowAll(prev => !prev);
  }, []);

  const openImageModal = useCallback((image) => {
    setSelectedImage(image);
  }, []);

  const closeImageModal = useCallback(() => {
    setSelectedImage(null);
  }, []);

  const openCurrentSlideImage = useCallback(() => {
    const activeImage = mainImages[currentSlide];
    if (activeImage) {
      openImageModal(activeImage);
    }
  }, [mainImages, currentSlide, openImageModal]);

  const handleImageKeyDown = useCallback((event, image) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openImageModal(image);
    }
  }, [openImageModal]);

  const handleSliderKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openCurrentSlideImage();
    }
  }, [openCurrentSlideImage]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!showAll) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            observer.unobserve(img);
          }
        });
      },
      {
        rootMargin: '50px 0px',
        threshold: 0.1
      }
    );

    document.querySelectorAll('.grid-image[data-src]').forEach(img => {
      observer.observe(img);
    });

    return () => observer.disconnect();
  }, [showAll]);

  useEffect(() => {
    if (!selectedImage) return;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeImageModal();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedImage, closeImageModal]);

  return (
    <section className="gallery" id="gallery">
      <div className="container">
        <div className="section-title">
          <h2>Our Work</h2>
          <p>Browse through our portfolio of finished landscape transformations</p>
        </div>
        
        {imagesLoaded ? (
          <div className="gallery-slider">
            <Slider {...settings}>
              {mainImages.map((image, index) => (
                <div key={image.url} className="slide-item">
                  <div
                    className="slide-image"
                    role="button"
                    tabIndex={0}
                    onClick={openCurrentSlideImage}
                    onKeyDown={handleSliderKeyDown}
                    aria-label="Open full image"
                  >
                    <div className="lazy-image-wrapper">
                      <LazyLoadImage
                        src={image.url}
                        alt={image.title}
                        width={image.width}
                        height={image.height}
                        className="lazy-image"
                        style={{
                          objectFit: image.fit || 'cover',
                          objectPosition: image.position || 'center'
                        }}
                        effect="blur"
                        afterLoad={() => {
                          if (index === currentSlide) {
                            const img = document.querySelector(`[src="${image.url}"]`);
                            if (img) img.style.opacity = 1;
                          }
                        }}
                        beforeLoad={() => {
                          if (index === currentSlide) {
                            const img = document.querySelector(`[src="${image.url}"]`);
                            if (img) img.style.opacity = 0;
                          }
                        }}
                        wrapperClassName="lazy-image-wrapper"
                      />
                    </div>
                    <div className="slide-content">
                      <h3>{image.title}</h3>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          </div>
        ) : (
          <div className="gallery-slider">
            <div className="slide-item">
              <div className="slide-image">
                <div className="lazy-image-wrapper" />
              </div>
            </div>
          </div>
        )}

        <div className="gallery-view-more">
          <button onClick={toggleShowAll} className="view-more-button">
            {showAll ? 'Show Less' : 'View More Projects'}
          </button>
          
          {showAll && (
            <div className="additional-images">
              {additionalImages.map((image) => (
                <div key={image.url} className="gallery-grid-item">
                  <div
                    className="grid-image-wrapper"
                    role="button"
                    tabIndex={0}
                    onClick={() => openImageModal(image)}
                    onKeyDown={(event) => handleImageKeyDown(event, image)}
                    aria-label={`Open full image: ${image.title}`}
                  >
                    <LazyLoadImage
                      src={image.url}
                      alt={image.title}
                      width={image.width}
                      height={image.height}
                      className="grid-image"
                      effect="blur"
                      wrapperClassName="grid-image-wrapper"
                    />
                  </div>
                  <div className="grid-content">
                    <h3>{image.title}</h3>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedImage && (
        <div className="image-modal-overlay" onClick={closeImageModal} role="presentation">
          <div className="image-modal-content" onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-label={selectedImage.title}>
            <button type="button" className="image-modal-close" onClick={closeImageModal} aria-label="Close full-size image">
              x
            </button>
            <img src={selectedImage.url} alt={selectedImage.title} className="image-modal-full" />
            <p className="image-modal-title">{selectedImage.title}</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default React.memo(Gallery); 